import React, { useState } from "react";

export default function HardcopyRewardsShelf({ streaks, claimedHardcopies, claimHardcopy, setActiveClaimPass }) {
  const [selectedRewardIndex, setSelectedRewardIndex] = useState(0);
  const [hoveredRewardIndex, setHoveredRewardIndex] = useState(null);

  const shelfMilestones = [
    {
      days: 7,
      rewardId: "hc-paperback",
      title: "Paperback Reward",
      subtitle: "Claim any standard paperback",
      longDescription: "Earn a physical paperback copy of any book from our bookstore. Reach a 7-day reading streak to unlock this reward voucher code.",
      coverGradient: "linear-gradient(135deg, #a87922 0%, #523b10 100%)",
      spineGradient: "linear-gradient(to bottom, #523b10, #36260a)",
      icon: "📖"
    },
    {
      days: 14,
      rewardId: "hc-premium",
      title: "Premium Reward",
      subtitle: "Claim any premium paperback",
      longDescription: "Earn a physical premium-edition paperback of any book. Reach a 14-day reading streak to unlock this reward voucher code.",
      coverGradient: "linear-gradient(135deg, #1C355E 0%, #0A162B 100%)",
      spineGradient: "linear-gradient(to bottom, #0A162B, #050b14)",
      icon: "👑"
    },
    {
      days: 30,
      rewardId: "hc-deluxe",
      title: "Deluxe Hardcover",
      subtitle: "Claim any hardcover / study edition",
      longDescription: "Earn a premium physical hardcover, study edition, or collector's book. Reach a 30-day reading streak to unlock this reward voucher code.",
      coverGradient: "linear-gradient(135deg, #2D1A04 0%, #110B03 100%)",
      spineGradient: "linear-gradient(to bottom, #110B03, #0d0904)",
      icon: "🏆"
    }
  ];

  const selectedMilestone = shelfMilestones[selectedRewardIndex];
  const maxStreak = Math.max(streaks.currentStreak || 0, streaks.longestStreak || 0);
  const isSelectedUnlocked = maxStreak >= selectedMilestone.days;
  const selectedClaimInfo = claimedHardcopies[selectedMilestone.rewardId];
  const isSelectedClaimed = !!selectedClaimInfo;

    return (
    <div style={{
      background: "var(--white)",
      border: "1px solid var(--dust)",
      borderRadius: "16px",
      padding: "1.75rem",
      boxShadow: "var(--shadow)",
      fontFamily: "'Outfit', sans-serif",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    }}>
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.5rem", flexWrap: "wrap", gap: "0.5rem" }}>
          <h3 style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "1.35rem",
            color: "var(--ink)",
            fontWeight: 600,
            margin: 0,
          }}>
            Physical Hardcopy Rewards
          </h3>
          <span style={{
            fontSize: "0.82rem",
            color: "var(--gold)",
            fontWeight: 600,
            background: "var(--cream)",
            padding: "3px 8px",
            borderRadius: "12px",
            border: "1px solid var(--dust)",
          }}>
            Streak: {streaks.currentStreak || 0}d
          </span>
        </div>
        <p style={{
          fontSize: "0.82rem",
          color: "var(--muted)",
          margin: "0 0 1.25rem 0",
          lineHeight: 1.4,
        }}>
          Unlock physical books at the bookstore by maintaining reading streaks. Tap any book cover to claim.
        </p>

        {/* 3D Bookshelf display */}
        <div style={{
          position: "relative",
          background: "var(--hero-bg)",
          border: "1px solid var(--dust)",
          borderRadius: "16px",
          padding: "1.5rem 1rem 3rem",
          height: "200px",
          display: "flex",
          justifyContent: "space-around",
          alignItems: "flex-end",
          boxShadow: "inset 0 4px 20px rgba(0,0,0,0.05)",
          overflow: "visible",
          boxSizing: "border-box",
        }}>
          {/* Shelf side shadow */}
          <div style={{
            position: "absolute",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "linear-gradient(to right, rgba(0,0,0,0.05) 0%, transparent 15%, transparent 85%, rgba(0,0,0,0.05) 100%)",
            pointerEvents: "none",
            zIndex: 1,
          }} />

          {/* Wooden Board */}
          <div style={{
            position: "absolute",
            bottom: "16px",
            left: "6px",
            right: "6px",
            height: "10px",
            background: "linear-gradient(to bottom, #8E5A23 0%, #523414 100%)",
            borderRadius: "3px",
            boxShadow: "0 6px 12px rgba(0,0,0,0.3)",
            zIndex: 3,
          }} />

          {/* Drop shadow of board */}
          <div style={{
            position: "absolute",
            bottom: "8px",
            left: "12px",
            right: "12px",
            height: "8px",
            background: "rgba(0,0,0,0.1)",
            filter: "blur(4px)",
            zIndex: 2,
          }} />

          {/* 3D Books map */}
          {shelfMilestones.map((milestone, idx) => {
            const isMilestoneUnlocked = maxStreak >= milestone.days;
            const claimInfo = claimedHardcopies[milestone.rewardId];
            const isClaimed = !!claimInfo;
            const isSelected = selectedRewardIndex === idx;
            const isHovered = hoveredRewardIndex === idx;

            return (
              <div
                key={milestone.days}
                onClick={() => setSelectedRewardIndex(idx)}
                onMouseEnter={() => setHoveredRewardIndex(idx)}
                onMouseLeave={() => setHoveredRewardIndex(null)}
                style={{
                  perspective: "600px",
                  width: "76px",
                  height: "110px",
                  cursor: "pointer",
                  position: "relative",
                  zIndex: isSelected || isHovered ? 10 : 4,
                  transition: "transform 0.3s ease",
                }}
              >
                {/* 3D Book Cover Canvas */}
                <div style={{
                  width: "100%",
                  height: "100%",
                  position: "relative",
                  transformStyle: "preserve-3d",
                  transition: "transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)",
                  transform: isSelected 
                    ? "rotateY(-25deg) translateZ(12px) scale(1.08)" 
                    : isHovered 
                    ? "rotateY(-15deg) translateZ(6px) scale(1.03)" 
                    : "rotateY(-5deg)",
                  boxShadow: isSelected
                    ? "0 10px 22px rgba(200, 146, 42, 0.15), 8px 8px 16px rgba(0,0,0,0.2)"
                    : isHovered
                    ? "5px 5px 12px rgba(0,0,0,0.15)"
                    : "3px 3px 6px rgba(0,0,0,0.15)",
                }}>
                  {/* Front Face */}
                  <div style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    left: 0,
                    top: 0,
                    background: milestone.coverGradient,
                    borderRadius: "0 3px 3px 0",
                    padding: "8px 4px",
                    boxSizing: "border-box",
                    borderLeft: "4px solid rgba(0,0,0,0.3)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    backfaceVisibility: "hidden",
                    zIndex: 5,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    alignItems: "center",
                    textAlign: "center",
                  }}>
                    {/* Foil border lines */}
                    <div style={{
                      position: "absolute",
                      top: "3px", left: "6px", right: "3px", bottom: "3px",
                      border: "1px solid rgba(250,240,220,0.25)",
                      borderRadius: "0 2px 2px 0",
                      pointerEvents: "none",
                    }} />

                    <div style={{ fontSize: "1.1rem", marginTop: "2px", filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))" }}>
                      {milestone.icon}
                    </div>

                    <div>
                      <div style={{
                        fontFamily: "'Outfit', sans-serif",
                        fontSize: "0.6rem",
                        fontWeight: 700,
                        color: "#fff",
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        opacity: 0.9,
                      }}>
                        {milestone.days}d Streak
                      </div>
                      <div style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        color: "#FAF7F2",
                        lineHeight: 1.1,
                        textTransform: "uppercase",
                        marginTop: "1px",
                      }}>
                        {milestone.rewardId === "hc-paperback" ? "Paperback" : milestone.rewardId === "hc-premium" ? "Premium" : "Deluxe"}
                      </div>
                    </div>

                    <div style={{
                      fontSize: "0.5rem",
                      color: "rgba(255,255,255,0.55)",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      fontWeight: 700,
                    }}>
                      Unlock
                    </div>
                  </div>

                  {/* Spine edge */}
                  <div style={{
                    position: "absolute",
                    width: "12px",
                    height: "100%",
                    left: "-6px",
                    top: 0,
                    background: milestone.spineGradient,
                    transform: "rotateY(-90deg) translateZ(6px)",
                    boxShadow: "inset -2px 0 5px rgba(0,0,0,0.5)",
                    borderRadius: "1px 0 0 1px",
                  }} />

                  {/* Pages edge */}
                  <div style={{
                    position: "absolute",
                    width: "10px",
                    height: "calc(100% - 4px)",
                    right: "-5px",
                    top: "2px",
                    background: "#FAF7F2",
                    backgroundImage: "linear-gradient(to right, #E8E0D5 1px, transparent 1px)",
                    backgroundSize: "2px 100%",
                    transform: "rotateY(90deg) translateZ(5px)",
                    borderRadius: "0 1px 1px 0",
                  }} />
                </div>

                {/* Locked shade */}
                {!isMilestoneUnlocked && (
                  <div style={{
                    position: "absolute",
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: "rgba(20, 15, 10, 0.65)",
                    backdropFilter: "grayscale(1) blur(0.4px)",
                    borderRadius: "0 3px 3px 0",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "#fff",
                    zIndex: 8,
                    pointerEvents: "none",
                  }}>
                    <span style={{ fontSize: "1.1rem", filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.6))" }}>🔒</span>
                    <span style={{ fontSize: "0.55rem", marginTop: "1px", fontWeight: 700, letterSpacing: "0.03em", background: "var(--ink)", padding: "1px 4px", borderRadius: "4px", color: "var(--cream)" }}>
                      {milestone.days}d
                    </span>
                  </div>
                )}

                {/* Claimed badge */}
                {isClaimed && (
                  <div style={{
                    position: "absolute",
                    top: "8px",
                    left: "-6px",
                    background: "var(--sage)",
                    color: "#fff",
                    fontSize: "0.52rem",
                    fontWeight: 800,
                    padding: "1px 4px",
                    borderRadius: "2px",
                    boxShadow: "1px 1px 4px rgba(0,0,0,0.4)",
                    transform: "rotate(-10deg)",
                    zIndex: 9,
                    pointerEvents: "none",
                  }}>
                    CLAIMED
                  </div>
                )}

                {/* Ready pulse */}
                {isMilestoneUnlocked && !isClaimed && (
                  <div style={{
                    position: "absolute",
                    bottom: "-4px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "var(--gold)",
                    color: "var(--white)",
                    fontSize: "0.5rem",
                    fontWeight: 800,
                    padding: "1px 4px",
                    borderRadius: "3px",
                    boxShadow: "0 0 8px var(--gold)",
                    zIndex: 9,
                    animation: "bounceMini 0.8s infinite alternate",
                    pointerEvents: "none",
                  }}>
                    READY
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Info Panel under shelf */}
        <div style={{
          background: "var(--cream)",
          border: "1px solid var(--dust)",
          borderRadius: "12px",
          padding: "1rem 1.25rem",
          marginTop: "1.25rem",
          boxShadow: "inset 0 2px 4px rgba(0,0,0,0.01)",
          display: "flex",
          flexDirection: "column",
          gap: "0.6rem",
          animation: "fadeUp 0.3s ease both",
        }}>
          <div style={{ display: "flex", justify: "space-between", alignItems: "flex-start" }}>
            <div>
              <span style={{
                fontSize: "0.68rem",
                fontWeight: 700,
                color: isSelectedUnlocked ? "var(--gold)" : "var(--muted)",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}>
                {selectedMilestone.days}-Day Streak Milestone
              </span>
              <h4 style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "1.15rem",
                fontWeight: 700,
                color: "var(--ink)",
                margin: "1px 0 0 0",
              }}>
                {selectedMilestone.title}
              </h4>
            </div>
            <span style={{
              fontSize: "0.72rem",
              color: isSelectedUnlocked ? "var(--sage)" : "var(--muted)",
              fontWeight: 700,
              background: isSelectedUnlocked ? "rgba(74, 124, 89, 0.08)" : "var(--cream)",
              padding: "2px 8px",
              borderRadius: "10px",
              border: `1px solid ${isSelectedUnlocked ? "rgba(74, 124, 89, 0.15)" : "var(--dust)"}`,
            }}>
              {isSelectedUnlocked ? "🔓 Unlocked" : "🔒 Locked"}
            </span>
          </div>

          <p style={{
            fontSize: "0.8rem",
            color: "var(--muted)",
            lineHeight: 1.4,
            margin: 0,
          }}>
            {selectedMilestone.longDescription}
          </p>

          <div style={{
            marginTop: "0.2rem",
            paddingTop: "0.6rem",
            borderTop: "1px solid var(--dust)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "0.75rem",
            flexWrap: "wrap",
          }}>
            {!isSelectedUnlocked ? (
              <>
                <div style={{ flex: 1, minWidth: "120px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.72rem", color: "var(--muted)", marginBottom: "4px" }}>
                    <span>Streak status</span>
                    <strong>{maxStreak} / {selectedMilestone.days} days</strong>
                  </div>
                  <div style={{ width: "100%", height: "6px", background: "var(--dust)", borderRadius: "3px", overflow: "hidden" }}>
                    <div style={{
                      width: `${Math.min(100, (maxStreak / selectedMilestone.days) * 100)}%`,
                      height: "100%",
                      background: "var(--gold)",
                      borderRadius: "3px",
                    }} />
                  </div>
                </div>
                <span style={{
                  fontSize: "0.72rem",
                  color: "var(--muted)",
                  background: "var(--cream)",
                  padding: "2px 8px",
                  borderRadius: "6px",
                  border: "1px solid var(--dust)",
                  fontWeight: 600,
                }}>
                  {selectedMilestone.days - maxStreak}d left
                </span>
              </>
            ) : (
              <>
                <div style={{ fontSize: "0.76rem", color: "var(--muted)" }}>
                  {isSelectedClaimed ? (
                    <span>Voucher: <strong style={{ color: "var(--ink)", fontFamily: "monospace" }}>{selectedClaimInfo.code}</strong></span>
                  ) : (
                    <span style={{ color: "var(--sage)", fontWeight: 600 }}>✨ Unlocked! Present voucher at the counter.</span>
                  )}
                </div>

                <div>
                  {isSelectedClaimed ? (
                    <button
                      onClick={() => {
                        setActiveClaimPass({ rewardId: selectedMilestone.rewardId, title: selectedMilestone.title, subtitle: selectedMilestone.subtitle, ...selectedClaimInfo });
                      }}
                      style={{
                        background: "var(--cream)",
                        border: "1px solid var(--sage)",
                        color: "var(--sage)",
                        borderRadius: "6px",
                        padding: "5px 12px",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        fontFamily: "'Outfit', sans-serif",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "var(--dust)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "var(--cream)";
                      }}
                    >
                      View Ticket 🎫
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        claimHardcopy(selectedMilestone.rewardId, selectedMilestone.days, selectedMilestone.title, selectedMilestone.subtitle);
                      }}
                      style={{
                        background: "var(--gold)",
                        border: "none",
                        color: "var(--white)",
                        borderRadius: "6px",
                        padding: "5px 12px",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        fontFamily: "'Outfit', sans-serif",
                        boxShadow: "var(--shadow)",
                        transition: "transform 0.15s, opacity 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = 0.95;
                        e.currentTarget.style.transform = "scale(1.02)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = 1;
                        e.currentTarget.style.transform = "none";
                      }}
                    >
                      Claim Pass 🎁
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
